import { useMemo } from 'react';
import { useQueries } from 'react-query';

import { useCurrentAddressInfo, useLoginedAddresses } from '@/hooks';
import { getCredential, request } from '@/utils';
import type { HiUserInfo } from '@/views/Chats/types';

const HI_NOTI_URL = '/hi/unread_message_count';

const STREAM_NOTI_URL = '/feed/last_is_read';

const NOTI_FETCH_INTERVAL = 10 * 1000;

export const useAccountsNotification = () => {
  const { address } = useCurrentAddressInfo();
  const loginedList = useLoginedAddresses();

  const otherAccounst = useMemo(
    () => loginedList?.filter(x => x.id !== address),
    [address, loginedList],
  );

  const hiRes = useQueries(
    otherAccounst.map(x => {
      const credential = getCredential(x.id);

      return {
        queryKey: [HI_NOTI_URL, x.id, address],
        queryFn: () =>
          request
            .get<Pick<HiUserInfo, 'unread_message_count'>>(HI_NOTI_URL, undefined, {
              credential,
            })
            .then(r => ({ [x.id]: !!r.unread_message_count })),
        refetchInterval: NOTI_FETCH_INTERVAL,
        refetchOnWindowFocus: true,
        cacheTime: NOTI_FETCH_INTERVAL,
        staleTime: NOTI_FETCH_INTERVAL,
        enabled: !!credential,
      };
    }),
  );

  const streamRes = useQueries(
    otherAccounst.map(x => {
      const credential = getCredential(x.id);

      return {
        queryKey: [STREAM_NOTI_URL, x.id, address],
        queryFn: () =>
          request
            .get<{ is_read: boolean }>(STREAM_NOTI_URL, undefined, {
              credential,
            })
            .then(r => ({ [x.id]: !r.is_read })),
        refetchInterval: NOTI_FETCH_INTERVAL,
        refetchOnWindowFocus: true,
        cacheTime: NOTI_FETCH_INTERVAL,
        staleTime: NOTI_FETCH_INTERVAL,
        enabled: !!credential,
      };
    }),
  );

  const hi = useMemo(
    () =>
      hiRes.reduce((m: Record<string, boolean>, x) => {
        Object.assign(m, x.data);

        return m;
      }, {}),
    [hiRes],
  );

  const stream = useMemo(
    () =>
      streamRes.reduce((m: Record<string, boolean>, x) => {
        Object.assign(m, x.data);

        return m;
      }, {}),
    [streamRes],
  );

  const hasNoti = useMemo(() => {
    return Object.keys(hi).reduce((m, id) => {
      // hi 有通知时显示红点
      return m || hi[id];
    }, false);
  }, [hi]);

  return { hi, stream, hasNoti };
};
